import { normalizeDurationToSeconds } from './mediaDuration';

export const AUDIO_UPLOAD_METADATA_KEY = '__audioUploadMetadata';
export const RECORDED_AUDIO_DISPLAY_NAME = 'Voice note.m4a';

const AUDIO_MIME_EXTENSION_MAP = Object.freeze({
    'audio/aac': 'aac',
    'audio/webm': 'webm',
    'audio/ogg': 'ogg',
    'audio/wav': 'wav',
    'audio/wave': 'wav',
    'audio/x-wav': 'wav',
    'audio/x-m4a': 'm4a',
    'audio/m4a': 'm4a',
    'audio/mp4': 'm4a',
    'audio/x-mp3': 'mp3',
    'audio/mp3': 'mp3',
    'audio/mpeg': 'mp3'
});

const AUDIO_MIME_CANONICAL_MAP = Object.freeze({
    'audio/x-m4a': 'audio/mp4',
    'audio/m4a': 'audio/mp4',
    'audio/mp4': 'audio/mp4',
    'video/mp4': 'audio/mp4',
    'audio/x-mp3': 'audio/mpeg',
    'audio/mp3': 'audio/mpeg',
    'audio/mpeg': 'audio/mpeg',
    'audio/mpga': 'audio/mpeg',
    'audio/x-wav': 'audio/wav',
    'audio/wave': 'audio/wav',
    'audio/wav': 'audio/wav'
});

export const normalizeAudioMimeType = mimeType => {
    if (!mimeType) {
        return '';
    }
    const normalized = String(mimeType).split(';')[0].trim().toLowerCase();
    return AUDIO_MIME_CANONICAL_MAP[normalized] || normalized;
};

export const getAudioExtensionFromMimeType = mimeType => {
    const normalizedMime = normalizeAudioMimeType(mimeType);
    return AUDIO_MIME_EXTENSION_MAP[normalizedMime] || 'webm';
};

export const normalizeDurationSeconds = value => {
    return normalizeDurationToSeconds(value, { unitHint: 'seconds' });
};

const normalizeMetadata = (metadata = {}, fallbackMimeType = '') => ({
    ...metadata,
    displayName: String(metadata.displayName || '').trim() || null,
    mimeType: normalizeAudioMimeType(metadata.mimeType || fallbackMimeType) || null,
    duration: normalizeDurationSeconds(metadata.duration)
});

const padTwo = value => String(value).padStart(2, '0');

export const buildRecordedAudioStorageName = ({ extension, now = new Date() }) => {
    const safeExtension = String(extension || 'm4a').replace(/^\./, '') || 'm4a';
    const timestamp =
        [
            now.getFullYear(),
            padTwo(now.getMonth() + 1),
            padTwo(now.getDate())
        ].join('-') +
        '_' +
        [padTwo(now.getHours()), padTwo(now.getMinutes()), padTwo(now.getSeconds())].join('-');

    return `voice_note_${timestamp}.${safeExtension}`;
};

export const buildRecordedAudioDisplayName = () => RECORDED_AUDIO_DISPLAY_NAME;

export const attachAudioUploadMetadata = (file, metadata = {}) => {
    if (!file || typeof file !== 'object') {
        return file;
    }

    const normalizedMetadata = normalizeMetadata(metadata, file.type);

    try {
        Object.defineProperty(file, AUDIO_UPLOAD_METADATA_KEY, {
            configurable: true,
            writable: true,
            value: normalizedMetadata
        });
    } catch (_) {
        file[AUDIO_UPLOAD_METADATA_KEY] = normalizedMetadata;
    }

    return file;
};

export const getAudioUploadMetadata = file => {
    if (!file || typeof file !== 'object') {
        return null;
    }

    const metadata = file[AUDIO_UPLOAD_METADATA_KEY];
    if (!metadata || typeof metadata !== 'object') {
        return null;
    }

    return normalizeMetadata(metadata, file.type);
};

export const resolveAudioDuration = async ({ providedDuration, fileDuration, extractDuration }) => {
    const metadataDuration = normalizeDurationSeconds(providedDuration);
    if (metadataDuration !== null) {
        return metadataDuration;
    }

    const attachedDuration = normalizeDurationSeconds(fileDuration);
    if (attachedDuration !== null) {
        return attachedDuration;
    }

    if (typeof extractDuration !== 'function') {
        return null;
    }

    try {
        const extractedDuration = await extractDuration();
        return normalizeDurationSeconds(extractedDuration);
    } catch (_) {
        return null;
    }
};
